/** Stele kind and tag constants */

export const STELE_PAGE_KIND = 35821;
export const STELE_SPACE_KIND = 39123;

export const STELE_CLIENT = 'stele';

/** Generate a d-tag for a page */
export function pageDTag(slug: string): string {
  return `stele:page:${slug}`;
}

/** Generate a d-tag for a space */
export function spaceDTag(slug: string): string {
  return `stele:space:${slug}`;
}

/** Extract slug from a page d-tag */
export function pageSlugFromDTag(dTag: string): string | undefined {
  const prefix = 'stele:page:';
  if (dTag.startsWith(prefix)) {
    return dTag.slice(prefix.length);
  }
  return undefined;
}

/** Extract slug from a space d-tag */
export function spaceSlugFromDTag(dTag: string): string | undefined {
  const prefix = 'stele:space:';
  if (dTag.startsWith(prefix)) {
    return dTag.slice(prefix.length);
  }
  return undefined;
}

/** Build an `a` tag coordinate for a space */
export function spaceCoordinate(pubkey: string, spaceSlug: string): string {
  return `${STELE_SPACE_KIND}:${pubkey}:${spaceDTag(spaceSlug)}`;
}

/** Slugify a title into a URL-safe slug */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Check if a string is a valid slug */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}

/** Maximum page tree depth */
export const MAX_PAGE_DEPTH = 3;

/** Get the depth of a page path (number of segments) */
export function getPathDepth(path: string): number {
  const clean = path.replace(/^\/+|\/+$/g, '');
  if (!clean) return 0;
  return clean.split('/').length;
}

/** Get the parent path of a page path */
export function getParentPath(path: string): string | null {
  const segments = path.replace(/^\/+|\/+$/g, '').split('/');
  if (segments.length <= 1) return null;
  return '/' + segments.slice(0, -1).join('/');
}

/** Page entry in a Space's page tree */
export interface PageEntry {
  /** The page d-tag, e.g. "stele:page:getting-started" */
  dTag: string;
  /** The page path within the space, e.g. "/getting-started" */
  path: string;
}

/** Parse page tags from a Space event's tags */
export function parsePageTags(tags: string[][]): PageEntry[] {
  return tags
    .filter(t => t[0] === 'page' && t.length >= 3)
    .map(t => ({
      dTag: t[1],
      path: t[2],
    }));
}

/** Build a tree structure from flat page entries */
export interface PageTreeNode {
  entry: PageEntry;
  children: PageTreeNode[];
}

export function buildPageTree(entries: PageEntry[]): PageTreeNode[] {
  const roots: PageTreeNode[] = [];
  const nodeMap = new Map<string, PageTreeNode>();

  // First pass: create all nodes
  for (const entry of entries) {
    const node: PageTreeNode = { entry, children: [] };
    nodeMap.set(entry.path, node);
  }

  // Second pass: build tree
  for (const entry of entries) {
    const node = nodeMap.get(entry.path)!;
    const parentPath = getParentPath(entry.path);
    if (parentPath && nodeMap.has(parentPath)) {
      nodeMap.get(parentPath)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

/** Flatten a tree back to ordered entries (pre-order traversal) */
export function flattenPageTree(nodes: PageTreeNode[]): PageEntry[] {
  const result: PageEntry[] = [];
  function walk(nodes: PageTreeNode[]) {
    for (const node of nodes) {
      result.push(node.entry);
      walk(node.children);
    }
  }
  walk(nodes);
  return result;
}

/** Check if an event is a tombstone (soft-deleted) */
export function isTombstone(tags: string[][]): boolean {
  return tags.some(t => t[0] === 'deleted' && t[1] === 'true');
}

/** Get tag value by name */
export function getTagValue(tags: string[][], name: string): string | undefined {
  return tags.find(t => t[0] === name)?.[1];
}

/**
 * Validate that an event is a genuine Stele event.
 * Checks kind, client tag, and d-tag prefix.
 * Used as a client-side guard after relay queries — relays may not
 * perfectly index multi-tag filters, so we always validate locally.
 */
export function isSteleSpace(event: { kind: number; tags: string[][] }): boolean {
  if (event.kind !== STELE_SPACE_KIND) return false;
  const client = getTagValue(event.tags, 'client');
  if (client !== STELE_CLIENT) return false;
  const d = getTagValue(event.tags, 'd');
  if (!d || !d.startsWith('stele:space:')) return false;
  return true;
}

export function isStelePage(event: { kind: number; tags: string[][] }): boolean {
  if (event.kind !== STELE_PAGE_KIND) return false;
  const client = getTagValue(event.tags, 'client');
  if (client !== STELE_CLIENT) return false;
  const d = getTagValue(event.tags, 'd');
  if (!d || !d.startsWith('stele:page:')) return false;
  return true;
}
